import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';

interface User {
  id: number;
  username: string;
  role: 'admin' | 'user';
  can_edit_self?: boolean;
  // يمكن إضافة المزيد من الخصائص حسب الحاجة
}

interface UserInfoProps {
  showLogout?: boolean;
  onLogout?: () => void;
}

function UserInfo({ showLogout = true, onLogout }: UserInfoProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = useCallback(() => {
    setIsLoading(true);
    try {
      const userJson = sessionStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('خطأ في قراءة بيانات المستخدم:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    try {
      // تنظيف sessionStorage
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('last_login');
      
      // استدعاء callback if provided
      if (onLogout) {
        onLogout();
      }
      
      // التوجيه لصفحة تسجيل الدخول
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('خطأ في تسجيل الخروج:', error);
    }
  }, [navigate, onLogout]);

  const handleEditProfile = useCallback(() => {
    navigate('/settings?tab=profile');
  }, [navigate]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginRight: '20px'
      }}>
        <div style={{ width: '20px', height: '20px' }} className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginRight: '20px'
      }}>
        <NotificationBell />
        <button
          onClick={() => navigate('/login')}
          style={{
            padding: '5px 10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#45a049'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
        >
          تسجيل الدخول
        </button>
      </div>
    );
  }

  const canEditSelf = user.can_edit_self || user.role === 'admin';
  const userRoleText = user.role === 'admin' ? 'مدير' : 'مستخدم';
  const userRoleColor = user.role === 'admin' ? '#4CAF50' : '#2196F3';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '8px 15px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'relative',
      marginRight: '20px',
      transition: 'box-shadow 0.3s ease'
    }}
    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
    onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}>
      
      <NotificationBell />
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px',
        padding: '2px 5px',
        borderRadius: '20px',
        transition: 'background-color 0.3s'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: userRoleColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold'
        }}>
          {user.username.charAt(0).toUpperCase()}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: '14px',
            lineHeight: '1.2'
          }}>
            {user.username}
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: userRoleColor,
            fontWeight: 'bold',
            lineHeight: '1.2'
          }}>
            {userRoleText}
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        {canEditSelf && (
          <button
            onClick={handleEditProfile}
            style={{
              padding: '6px 12px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1976D2';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2196F3';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '14px' }}>✏️</span>
            <span className="hide-on-mobile">تعديل بياناتي</span>
          </button>
        )}
        
        {showLogout && (
          <button
            onClick={handleLogout}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d32f2f';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f44336';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '14px' }}>🚪</span>
            <span className="hide-on-mobile">تسجيل خروج</span>
          </button>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .hide-on-mobile {
            display: none;
          }
          button {
            padding: 6px 8px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default UserInfo;